import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query'
import * as clients from 'shared/api/clients'
import * as users from 'shared/api/users'
import type * as fetch from 'shared/api/fetch'
import { isNilOrEmpty } from 'shared/utils'

const queryKeys = {
  userDetails: 'userDetails',
  userDetailsByEmails: 'userDetailsByEmails'
}

export const useQueryUserProfile = (
  userId: number | undefined,
  options?: { throwOnError?: boolean }
): UseQueryResult<clients.UserDetailsResp, fetch.ClientError> => {
  return useQuery({
    queryKey: [queryKeys.userDetails, userId],
    queryFn: async () => await users.getUserDetails(userId as number),
    enabled: userId != null,
    // Only override the app-wide `throwOnError` default when the caller explicitly
    // opts in/out — passing `throwOnError: undefined` here would otherwise win over
    // the queryClient default for every caller that doesn't pass this option at all.
    ...(options?.throwOnError !== undefined ? { throwOnError: options.throwOnError } : {})
  })
}

export const useQueryUserProfileFromEmail = (
  clientId: number,
  email?: string
): UseQueryResult<clients.UserDetailsResp | null, fetch.ClientError> => {
  return useQuery({
    queryKey: [queryKeys.userDetailsByEmails, email, clientId],
    queryFn: async () => await clients.getUserDetailsByEmail(email as string, clientId),
    enabled: !isNilOrEmpty(email),
    throwOnError: false
  })
}

export const useUpdateUserProfile = (
  userId: number | undefined
): UseMutationResult<clients.UserDetailsResp, fetch.ClientError, users.UpdateUserReq> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: users.updateUserProfile,
    onMutate: async (updatedData) => {
      await queryClient.cancelQueries({ queryKey: [queryKeys.userDetails] })
      queryClient.setQueryData(
        [queryKeys.userDetails, userId],
        (prevUserDetailsResp?: clients.UserDetailsResp | null) => {
          if (prevUserDetailsResp != null) {
            return {
              ...prevUserDetailsResp,
              ...updatedData
            }
          }
          return null
        }
      )
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.userDetails, userId] })
    }
  })
}
export const useDeleteUsers = (): UseMutationResult<users.UserResp, fetch.ClientError, number> => {
  return useMutation({ mutationFn: async (userId) => await users.del(userId) })
}
